import React, { useState, DragEvent, useEffect, useRef, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import ImageMarker, { Marker } from 'react-image-marker';

import { ReactComponent as CloseIcon } from '../../../assets/icons/close-circle.svg';
// import { ReactComponent as EditIcon } from '../../../assets/icons/edit-pencil-card.svg';
import { ReactComponent as EditPencilCard } from '../../../assets/icons/edit-pencil.svg';
import PumpIcon from '../../../assets/icons/gas-icon.svg';
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
import BackgroundImage from '../../../assets/images/background.png';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import MyButton from '../../../shared-components/button/Button';
import PageTitle from '../../../shared-components/page-title/PageTitle';
import SelectField from '../../../shared-components/select-field/SelectField';
import stationManagement from '../../../utils/api/station-management';

interface EditLaneStationProperties {
  onClose: () => void;
  initialPreview: string; // 🆕 Map URL or preview from backend
  initialMarkers: MarkerWithDevice[]; // 🆕 Marker list from backend
  handleLaneMapList: () => void;
}

interface LaneValues {
  selectedDevice: string;
  siteMapFile: File | null;
  siteMapPreview: string | null;
}

interface MarkerWithDevice extends Marker {
  device: string;
  chargingPointSn: string;
  position: {
    top: number;
    left: number;
  };
}

/**
 * Check the Image Url for valid
 * @param url
 * @returns boolean
 */
const isValidUrl = (url?: string): boolean => {
  if (!url) return false;
  try {
    return Boolean(new URL(url));
  } catch {
    return false;
  }
};

/**
 *  Edit Lane Station
 * @param param0
 * @returns
 */
function EditLaneStation({
  onClose,
  initialPreview,
  initialMarkers,
  handleLaneMapList,
}: EditLaneStationProperties) {
  const [laneValues, setLaneValues] = useState<LaneValues>({
    selectedDevice: '',
    siteMapFile: null,
    siteMapPreview: initialPreview || null,
  });
  const fileInputReference = useRef<HTMLInputElement | null>(null);
  const dispatch = useAppDispatch();
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);
  const [selectDevices, setSelectDevices] = useState(false);
  const [markers, setMarkers] = useState<MarkerWithDevice[]>(initialMarkers || []); // Add this state if missing
  const [loading, setLoading] = useState(false);
  const [deviceOptions, setDeviceOptions] = useState<{ label: string; value: string }[]>([]);
  const [loadedMaps, setLoadedMaps] = useState<Record<string, boolean>>({});
  const { t, i18n } = useTranslation('sidePanel');

  /**
   * devices list take Api
   */
  const handleDevicesList = async () => {
    try {
      const response = await stationManagement.fetchDevicesList({
        chargingStation: selectedChargingPoint?.id,
      });
      const formattedDevices = response.data.map((device: any) => ({
        label: device.chargingPointName,
        value: device.chargingPointSn,
      }));

      setDeviceOptions(formattedDevices);
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
    }
  };

  /** Available devices (exclude already assigned ones) */
  const availableDeviceOptions = deviceOptions.filter(
    (device) => !markers.some((marker) => marker.chargingPointSn === device.value),
  );

  /**
   *  add lane
   * @param name
   * @param value
   */
  const handleChangeFunction = (name: string, value: string) => {
    setLaneValues((previous) => ({ ...previous, [name]: value }));
    if (value) {
      setSelectDevices(false);
    }
  };

  /** Drag & Drop for new image */
  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setLaneValues({
        ...laneValues,
        siteMapFile: file,
        siteMapPreview: previewUrl,
      });
      setMarkers([]);
    }
  };
  /** Trigger hidden file input */
  const handleUploadClick = () => {
    fileInputReference.current?.click();
  };
  /** Handle file upload (input) */
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setLaneValues({
        selectedDevice: laneValues.selectedDevice,
        siteMapFile: file,
        siteMapPreview: previewUrl,
      });
      setMarkers([]);
    }
  };

  /**
   * remove for the images and devices
   */
  const handleRemoveImage = () => {
    setLaneValues({
      selectedDevice: '',
      siteMapFile: null,
      siteMapPreview: '',
    });
  };

  /** Add marker on image */
  const handleAddMarker = (marker: Marker) => {
    if (!laneValues.selectedDevice) {
      setSelectDevices(true);
      return;
    }

    const selectedDevice = deviceOptions.find(
      (device) => device.value === laneValues.selectedDevice,
    );

    const top = Number(marker.top);
    const left = Number(marker.left);

    const markerWithDevice: MarkerWithDevice = {
      top,
      left,
      device: selectedDevice?.label || laneValues.selectedDevice,
      chargingPointSn: selectedDevice?.value || '',
      position: {
        top,
        left,
      },
    };

    setMarkers((previous) => [...previous, markerWithDevice]);
    setLaneValues((previous) => ({ ...previous, selectedDevice: '' }));
  };

  /** Remove marker */
  const handleRemoveMarker = (markerToRemove: MarkerWithDevice) => {
    setMarkers((previous) =>
      previous.filter(
        (m) =>
          m.top !== markerToRemove.top ||
          m.left !== markerToRemove.left ||
          m.device !== markerToRemove.device,
      ),
    );
  };

  /** Save updated data */
  const handleSave = async () => {
    if (!selectedChargingPoint?.id) return;
    setLoading(true);
    const formData = new FormData();
    // Attach marker positions and device SNs
    markers.forEach((marker, index) => {
      formData.append(`chargingPointLocations[${index}][chargingPointSn]`, marker.chargingPointSn);
      formData.append(`chargingPointLocations[${index}][position][top]`, marker.top.toString());
      formData.append(`chargingPointLocations[${index}][position][left]`, marker.left.toString());
    });

    try {
      await stationManagement.editLaneMap(selectedChargingPoint?.id, formData);
      await handleLaneMapList(); // call async function when needed
      onClose(); // close modal after save
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
    }
  };
  useEffect(() => {
    handleDevicesList();
  }, []);
  return (
    <div className="add-lane-content" dir={i18n.dir()}>
      <div className="heading">
        <PageTitle
          title={t('editLane')}
          buttonSvgIcon={<CloseIcon />}
          buttonType="none"
          onClickButton={onClose}
          buttonLabel=" "
        />
      </div>

      <div className="select-devices">
        <SelectField
          options={availableDeviceOptions}
          placeholder={t('selectDevice')}
          selectedValue={laneValues.selectedDevice}
          name="selectedDevice"
          onChangeValue={handleChangeFunction}
          error={selectDevices}
          errorMessage={t('selectDeviceError')}
        />
        <div className="add-device">
          <MyButton
            buttonType="none"
            svgIcon={<EditPencilCard className="edit-icon" onClick={handleRemoveImage} />}
            label=""
          />
          <MyButton
            label={t('save')}
            buttonType="submit"
            onClickFunc={handleSave}
            loading={loading}
          />
        </div>
      </div>

      <div
        className="main-container"
        onDrop={handleDrop}
        onDragOver={(even) => even.preventDefault()}>
        {laneValues.siteMapPreview ? (
          <div className="images-container">
            <img
              src={laneValues.siteMapPreview}
              alt="preload"
              style={{ display: 'none' }}
              onLoad={() => {
                const url = laneValues.siteMapPreview;
                if (url) {
                  setLoadedMaps((previous) => ({
                    ...previous,
                    [url]: true,
                  }));
                }
              }}
            />
            <ImageMarker
              src={
                isValidUrl(laneValues.siteMapPreview) && loadedMaps[laneValues.siteMapPreview]
                  ? laneValues.siteMapPreview
                  : BackgroundImage
              }
              markers={markers}
              onAddMarker={handleAddMarker}
              markerComponent={(properties: Marker) => {
                const m = properties as MarkerWithDevice;
                return (
                  <div className="marker-wrapper">
                    <div className="device-marker">
                      <img src={PumpIcon} alt="" className="device-details-image" />
                      <p className="marker-device-label">
                        {deviceOptions.find((d) => d.value === m.chargingPointSn)?.label ||
                          m.device}
                      </p>
                      <div
                        role="button"
                        tabIndex={0}
                        className="remove-marker-button"
                        aria-label="remove-marker-button"
                        onClick={() => handleRemoveMarker(m)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') handleRemoveMarker(m);
                        }}>
                        <CloseIcon />
                      </div>
                    </div>
                    <div className="device-status-container online">
                      <div className="static-container" />
                      <div className="animation-container" />
                    </div>
                  </div>
                );
              }}
            />
          </div>
        ) : (
          <>
            <div
              className="upload-container"
              role="button"
              tabIndex={0}
              onClick={handleUploadClick}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') handleUploadClick();
              }}>
              <p className="upload">{t('dragDrop')}</p>
            </div>

            <div className="upload-footer">
              <p>{t('uploadSiteMapHint')}</p>
              <span
                className="upload-label"
                role="button"
                tabIndex={0}
                onClick={handleUploadClick}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') handleUploadClick();
                }}>
                <PlusIcon /> {t('uploadSiteMap')}
              </span>

              <input
                type="file"
                accept="image/*"
                ref={fileInputReference}
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default EditLaneStation;
